import { type GraphQLAnswer, graphql } from './client';

/**
 * Setting a password from an invitation link, the one call made WITHOUT a token.
 *
 * **Attention, written with `fetch`, deliberately, and not with Apollo.** `account` carries
 * no GraphQL client at all (no `@apollo/client`, no `graphql`, no codegen) where
 * `client` and `hub-admin` carry all three. Bringing that machinery in for a single
 * unauthenticated mutation would mean a provider wrapped around the whole
 * application, private routes included, plus a codegen configuration to maintain,
 * for one request that needs neither cache nor normalisation.
 *
 * The day a second call lands in `account`, that is the moment to bring Apollo in
 * and to delete this file: not before.
 */

/** Refusals `activateAccount` raises, each in `extensions.code`. */
export enum ActivationRefusal {
    /** No invitation matches this token, or it was revoked. */
    INVALID_LINK = 'INVALID_ACTIVATION_LINK',
    /** Past `expires_at`. Only a fresh invitation repairs it. */
    EXPIRED_LINK = 'EXPIRED_ACTIVATION_LINK',
    /** The invitation was already consumed: the person has a password. */
    ALREADY_ACTIVATED = 'ALREADY_ACTIVATED',
    /** The person was disabled between the invitation and the click. */
    ACCOUNT_DISABLED = 'ACCOUNT_DISABLED',
    /** The identity service is down or unconfigured. The only transient one. */
    IDENTITY_UNAVAILABLE = 'INSIDE_API_UNAVAILABLE',
    /** The pool refused the password. Repairs by choosing another one. */
    PASSWORD_REJECTED = 'PASSWORD_REJECTED',
}

export interface ActivationResult {
    email: string | null;
    /** Accounts the activation opened, named so the person recognises them. */
    organizationNames: string[];
}

/**
 * Carries the business code when the API named one.
 *
 * `code` is null for everything else: a network failure, a 500, a validation error
 * raised before our resolver. The screen renders those as an unexpected failure
 * rather than guessing which refusal they resemble.
 */
export class ActivationError extends Error {
    constructor(readonly code: ActivationRefusal | null) {
        super(code ?? 'UNKNOWN');
    }
}

const MUTATION = `
    mutation ActivateAccount($input: ActivateAccountInput!) {
        activateAccount(input: $input) {
            email
            organizationNames
        }
    }
`;

const isRefusal = (value: unknown): value is ActivationRefusal =>
    Object.values(ActivationRefusal).includes(value as ActivationRefusal);

export const activateAccount = async (token: string, password: string): Promise<ActivationResult> => {
    let payload: GraphQLAnswer<{ activateAccount?: ActivationResult | null }>;
    try {
        // Aucun jeton d'authentification, et c'est le point : c'est la mutation par
        // laquelle on entre, elle ne peut donc pas exiger d'etre deja entre.
        payload = await graphql<{ activateAccount?: ActivationResult | null }>(MUTATION, {
            input: { token, password },
        });
    } catch {
        throw new ActivationError(null);
    }

    const code = payload.errors?.[0]?.extensions?.code;
    if (code) {
        throw new ActivationError(isRefusal(code) ? code : null);
    }
    if (payload.errors?.length) {
        throw new ActivationError(null);
    }

    const result = payload.data?.activateAccount;
    if (!result) {
        throw new ActivationError(null);
    }
    return { email: result.email ?? null, organizationNames: result.organizationNames ?? [] };
};
